import { useEffect } from "react";
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { InputLabel, FormControl, Button, FormControlLabel } from '@mui/material';

import TextFieldEnter from "~/mui/TextFieldEnter";
import { MySelect, MyMenuItem } from "~/mui/MySelect";
import { checkEmail } from '~/utils/utils';
import { useTrans } from '~/hooks/useTrans';

import { apiInviteUserProject, apiAddUserProject } from "~/providers/api";
import { IUrlParams } from "~/interfaces";
import { TUserKeyRole } from "~/providers/interfaces";
import MyTitle from "~/mui/MyTitle";
import { myUseState } from "~/hooks/myUseState";
import { MySwitch } from "~/mui/MySwitch";
import { useLang } from "~/hooks/useLang";

import "./style.less";

interface IState {
	emailErrorMsg: string;
	email: string;
	roleType: TUserKeyRole;
	showOwner: boolean;
	userLanguage: string;
	emailNotifications: boolean;
}

interface IInviteUserForm {
	isInternal: boolean;
	ownerEmail: string;
	params: IUrlParams;
	projectLastUpdate: string;
	onInvite: (msg: string, lastUpdate: string) => void;
	onError: (errorMsg: string) => void;
}

export default function InviteUserForm({
	isInternal = false,
	ownerEmail = "",
	params = null,
	projectLastUpdate = "",
	onInvite = () => {},
	onError = () => {},
}: IInviteUserForm) {
	const { trans, transUserRole } = useTrans();
	const { lang } = useLang();
	const { state, setState, updateState } = myUseState<IState>({
		emailErrorMsg: "",
		email: "",
		roleType: "admin",
		showOwner: false,
		userLanguage: lang,
		emailNotifications: true,
	});

	async function inviteUser() {
		const email = state.email.trim();

		if (checkEmail(email)) {
			let errors: Array<string> = [];
			let lastUpdate = "";

			if (isInternal) {
				const addUserData = await apiAddUserProject(params.projectId, {
					email,
					role: state.roleType,
					emailNotifications: state.emailNotifications,
					userLanguage: state.userLanguage,
					lastUpdate: projectLastUpdate,
				}, lang);

				if (addUserData.noErrors) {
					lastUpdate = addUserData.lastUpdate;
				} else {
					errors = addUserData.errors;
				}
			} else {
				const inviteData = await apiInviteUserProject(params.projectId, {
					emailLanguage: state.userLanguage,
					invitedEmail: email,
					invitedRole: state.roleType,
					lastUpdate: projectLastUpdate,
				}, lang);

				if (inviteData.noErrors) {
					lastUpdate = inviteData.lastUpdate;
				} else {
					errors = inviteData.errors;
				}
			}

			if (errors.length === 0) {
				// upravime stav
				setState(prev => ({
					...prev,
					email: "",
					emailErrorMsg: "",
					projectLastUpdate: lastUpdate,
				}));
				onInvite(isInternal ? trans("usersPage.userWasAdded") : trans("usersPage.userWasInvited"), lastUpdate);
			} else {
				onError(errors.join(", "));
			}
		} else {
			updateState({
				emailErrorMsg: trans("userRole.error.email"),
			});
		}
	}

	useEffect(() => {
		updateState({
			roleType: ownerEmail === "" ? "owner" : "admin",
			showOwner: ownerEmail === "",
		});
	}, [ownerEmail]);

	return <>
		<MyTitle className="margin-bottom-large">
			{ trans("usersPage.inviteUserTitle") }
		</MyTitle>
		<div className="inviteUserForm__controls">
			<FormControl className="usersPage__responsiveMinWidth">
				<TextFieldEnter
					label={trans("usersPage.emailPlaceholder")}
					value={state.email}
					onEnter={() => inviteUser()}
					onChange={event => updateState({ email: event.target.value })}
					color="success"
					size="small"
					sx={{ minWidth: "300px" }}
					placeholder={trans("usersPage.emailPlaceholder")}
					{ ...state.emailErrorMsg
						? {
							error: true,
							helperText: state.emailErrorMsg,
						}
						: {}
					}
				/>
			</FormControl>
			<FormControl size="small" className="usersPage__responsiveMinWidth">
				<InputLabel>{ trans("userRole.role") }</InputLabel>
				<MySelect
					value={state.roleType}
					onChange={event => updateState({ roleType: event.target.value as TUserKeyRole })}
					label={ trans("type") }
				>
					{ state.showOwner && <MyMenuItem value="owner">{ transUserRole("owner") }</MyMenuItem> }
					<MyMenuItem value="admin">
						{ transUserRole("admin") }
					</MyMenuItem>
					<MyMenuItem value="visitor">
						{ transUserRole("visitor") }
					</MyMenuItem>
				</MySelect>
			</FormControl>
			<FormControlLabel
				control={<MySwitch sx={{ m: 1 }} checked={state.emailNotifications} onChange={event => updateState({ emailNotifications: event.target.checked })} />}
				label={trans("usersPage.inviteUserEmailNotifications")}
				sx={{ marginBottom: "var(--margin-large)" }}
			/>
			<FormControl size="small">
				<Button color="success" startIcon={<AddCircleIcon />} variant="contained" onClick={inviteUser}>
					{ trans("usersPage.addUser") }
				</Button>
			</FormControl>
		</div>
	</>;
}
